import {
  fromCamel,
  toCamel,
  debounce,
  parseAttribute,
  isIterable,
} from '@utils/gw'
import { reduceObject, deepMerge } from '@utils/transforms'
import { HTMLParsedElement } from '@lib/parsed-element'
import { getSectionId } from '@utils/shopify'

/**
 * GWElement
 *
 * @class
 * @abstracts
 */
export class GWElement extends HTMLParsedElement {
  constructor() {
    super()
    this.initialized = false
    this.options = {}
    this.listeners = new Map()
    this.attributeChangedCallbacks = new Map()
    this.hooks = { beforeListeners: [] }
    this.sectionId = getSectionId(this)
    this.optionObjectSeparator = '.'
    this.parsedOptionObjects = []
  }

  /**
   * Called when the element is parsed.
   *
   * Use in place of connectedCallback().
   */
  parsedCallback() {
    this.log('Connecting')

    this.init()
  }

  /**
   * Called when the element is disconnected from the DOM.
   */
  disconnectedCallback() {
    this.log('Disconnecting')

    this.destroy()
  }

  /**
   * Returns an array of attributes to observe.
   *
   * @returns {string[]}
   */
  static get observedAttributes() {
    return []
  }

  /**
   * Called when an element attribute is changed.
   *
   * @param {string} attr The attribute name.
   * @param {string} oldValue The old attribute value.
   * @param {string} newValue The new attribute value.
   */
  attributeChangedCallback(attr, oldValue, newValue) {
    if (oldValue === newValue) return

    const parsedValue = parseAttribute(newValue)

    const attrKey = toCamel(attr)
    // TODO: Trigger nested updates as well
    if (Object.keys(this.options).includes(attrKey)) {
      this.options[attrKey] = parsedValue
    }

    const attrCallback = this.attributeChangedCallbacks.get(attr)

    if (attrCallback) attrCallback(parsedValue, oldValue, attr)
  }

  /**
   * Adds an attribute changed listener.
   *
   * @param {string} attr The attribute name.
   * @param {Function} callback The attribute changed callback.
   */
  addAttributeListener(attr, callback) {
    this.attributeChangedCallbacks.set(attr, callback.bind(this))
  }

  /**
   * Adds event listeners.
   * @private
   */
  addEventListeners() {
    this.log('Adding event listeners')
    for (const [target, events] of this.listeners) {
      for (const [event, callback] of Object.entries(events)) {
        target.addEventListener(event, callback)
      }
    }
  }

  /**
   * Removes event listeners.
   * @private
   */
  removeEventListeners() {
    this.log('Removing event listeners')
    for (const [target, events] of this.listeners) {
      for (const [event, callback] of Object.entries(events)) {
        target.removeEventListener(event, callback)
      }
    }
  }

  /**
   * Adds an event listener.
   *
   * @param {Object} target The event target.
   * @param {string} event The event name.
   * @param {Function} callback The event callback.
   * @param {boolean} [bound=false] Indicates whether the callback has already been bound to the element instance.
   */
  addListener(target, event, callback, bound = false) {
    if (!target || !event) {
      return this.error('Failed to add listener, invalid target or event specified')
    }

    const existingListeners = this.listeners.get(target)
    const listeners = {
      ...existingListeners,
      [event]: bound ? callback : callback.bind(this),
    }

    this.listeners.set(target, listeners)
  }

  /**
   * Adds a delegated event listener.
   *
   * @param {Object} container The event container.
   * @param {string|NodeList} targets The event targets.
   * @param {string} event The event name.
   * @param {Function} callback The event callback.
   * @param {boolean} [bound=false] Indicates whether the callback has already been bound to the element instance.
   */
  addDelegatedListener(container, targets, event, callback, bound = false) {
    const targetsArray = Array.from(typeof targets === 'string' ? container.querySelectorAll(targets) : targets)
    const boundCallback = bound ? callback : callback.bind(this)

    const eventCallback = e => targetsArray.includes(e.target) && boundCallback(e)

    this.addListener(container, event, eventCallback)
  }

  /**
   * Adds a resize listener.
   *
   * @param {Object} target The resize target.
   * @param {Function} callback The resize callback.
   */
  addResizeListener(target, callback) {
    const debouncedCallback = debounce(callback.bind(this), RESIZE_DEBOUNCE_TIMER)

    this.addListener(target, 'resize', debouncedCallback, true)
  }

  /**
   * Adds a hook. Hooks are executed before/after lifecycle events.
   *
   * @param {string} hook The hook name.
   * @param {Function} callback The hook function.
   * @param {boolean} [bound=false] Indicates whether the hook function is bound to the element instance.
   */
  addHook(hook, callback, bound = false) {
    if (!Object.keys(this.hooks).includes(hook)) {
      return this.error('Failed to add hook, invalid hook specified')
    }

    if (!callback) {
      return this.error('Failed to add hook, no callback specified')
    }

    this.hooks[hook].push(bound ? callback : callback.bind(this))
  }

  /**
   * Registers a callback for use with Shopify Theme Editor events.
   *
   * @param {string} event - The event name.
   * @param {Function} callback - The event callback.
   * @param {boolean} [bound=false] - Indicates whether the callback has already been bound to the element instance.
   */
  registerEditorCallback(event, callback, bound = false) {
    if (!callback || typeof callback !== 'function' || callback.toString().endsWith('{}')) return

    const boundCallback = bound ? callback : callback.bind(this)

    const eventCallback = event => {
      if (event.detail.sectionId !== this.sectionId) return

      boundCallback(event.target, { ...event.detail, event })
    }

    this.addListener(document, event, eventCallback)
  }

  /**
   * Callback run when a block belonging to the component's section is selected in the theme editor
   *
   * @param {HTMLElement} element - The block element that was selected.
   * @param {Object} data - An object containing additional event data.
   * @param {boolean} data.load - A boolean indicating whether or not the selection was triggered by a section load event.
   * @param {Event} data.event - The event object.
   */
  onBlockSelect(element, { load, event }) { }

  /**
   * Callback run when a block belonging to the component's section is deselected in the theme editor
   *
   * @param {HTMLElement} element - The block element that was selected.
   * @param {Object} data - An object containing additional event data.
   * @param {Event} data.event - The event object.
   */
  onBlockDeselect(element, { event }) { }

  /**
   * Callback run when the component's section is loaded in the theme editor
   *
   * @param {HTMLElement} element - The section element that was loaded.
   * @param {Object} data - An object containing additional event data.
   * @param {Event} data.event - The event object.
   */
  onSectionLoad(element, { event }) { }

  /**
   * Callback run when the component's section is unloaded in the theme editor
   *
   * @param {HTMLElement} element - The section element that was unloaded.
   * @param {Object} data - An object containing additional event data.
   * @param {Event} data.event - The event object.
   */
  onSectionUnload(element, { event }) { }

  /**
   * Callback run when the component's section is selected in the theme editor
   *
   * @param {HTMLElement} element - The section element that was selected.
   * @param {Object} data - An object containing additional event data.
   * @param {boolean} data.load - A boolean indicating whether or not the selection was triggered by a section load event.
   * @param {Event} data.event - The event object.
   */
  onSectionSelect(element, { load, event }) { }

  /**
   * Callback run when the component's section is deselected in the theme editor
   *
   * @param {HTMLElement} element - The section element that was deselected.
   * @param {Object} data - An object containing additional event data.
   * @param {Event} data.event - The event object.
   */
  onSectionDeselect(element, { event }) { }

  /**
   * Callback run when the component's section is reordered in the theme editor
   *
   * @param {HTMLElement} element - The section element that was reordered.
   * @param {Object} data - An object containing additional event data.
   * @param {Event} data.event - The event object.
   */
  onSectionReorder(element, { event }) { }

  /**
   * Configures element for theme editor functionality.
   * Registers the necessary event callbacks for the supported editor events.
   */
  configureThemeEditor() {
    const editorEventCallbacks = {
      'shopify:block:select': this.onBlockSelect,
      'shopify:block:deselect': this.onBlockDeselect,
      'shopify:section:load': this.onSectionLoad,
      'shopify:section:unload': this.onSectionUnload,
      'shopify:section:select': this.onSectionSelect,
      'shopify:section:deselect': this.onSectionDeselect,
      'shopify:section:reorder': this.onSectionReorder,
    }

    for (const [e, c] of Object.entries(editorEventCallbacks)) {
      this.registerEditorCallback(e, c)
    }
  }

  /**
   * Initializes the element.
   */
  init() {
    if (this.initialized) return

    this.log('Initializing')

    this.options = this.getOptions()

    this.configureThemeEditor()

    for (const hook of this.hooks.beforeListeners) {
      hook()
    }

    this.addEventListeners()

    if (this.manualInit) return

    this.initialized = true
  }

  /**
   * Destroys the element.
   */
  destroy() {
    this.removeEventListeners()

    this.initialized = false
  }

  /**
   * Resets the element.
   */
  reset() {
    this.destroy()
    this.init()
  }

  /**
   * Resets the element on window resize.
   *
   * Usage: Call this method at the end of the constructor().
   */
  resetOnResize() {
    this.addResizeListener(window, this.reset)
  }

  /**
   * Returns the default element options.
   *
   * Usage: Override this method to provide element option structure.
   *        Option values will be populated automatically from attributes based on provided object keys.
   *
   * @abstract
   * @returns {Object}
   */
  get optionDefaults() {
    return {}
  }

  /**
   * Returns derived option values based on static element options.
   *
   * Usage: Override this method to provide additional element option structure.
   *        Derived option values can be constructed using the values of other static options.
   *
   * @abstract
   * @param {Object} options The element options.
   * @returns {Object}
   */
  derivedOptions(options) {
    return {}
  }

  /**
   * Returns the default option value based on the provided option name.
   *
   * @param {string} option The option name.
   * @returns {*}
   */
  optionDefault(option) {
    if (option.includes(this.optionObjectSeparator)) {
      const keys = option.split(this.optionObjectSeparator)

      let value = this.optionDefaults
      for (const key of keys) {
        value = value[key]
      }

      return value
    }

    return this.optionDefaults[option] ?? null
  }

  /**
   * Parses an element option.
   * @private
   *
   * @param {string} option The option name.
   * @returns {*}
   */
  parseOption(option) {
    const optionKey = fromCamel(option, '-')

    if (!this.hasAttribute(optionKey)) return null
    const value = this.getAttribute(optionKey)

    return parseAttribute(value)
  }

  /**
   * Parses an element option object.
   * @private
   *
   * @param {string} group The option group name.
   * @param {Object} [defaults={}] The default option values.
   * @returns {Object|boolean}
   */
  parseOptionObject(group, defaults = {}) {
    const baseObject = this.parseOption(group) ?? {}

    if (this.parseOption(group) === false) return false

    this.parsedOptionObjects.push(group)

    const objectPrefix = `${group}${this.optionObjectSeparator}`
    const optionAttributes = [baseObject]
    for (const attribute of this.attributes) {
      if (!attribute.name.startsWith(objectPrefix)) continue

      const parts = attribute.name.slice(objectPrefix.length).split(this.optionObjectSeparator)

      const value = this.parseOption(attribute.name)
      let attributeObject = value
      while (parts.length) {
        attributeObject = { [toCamel(parts.pop())]: attributeObject }
      }

      optionAttributes.push(attributeObject)
    }

    return deepMerge(defaults, ...optionAttributes)
  }

  /**
   * Returns the element options.
   * @private
   *
   * @returns {Object}
   */
  getOptions() {
    const defaults = this.optionDefaults
    const options = {}
    for (const [prop, defaultValue] of Object.entries(defaults)) {
      if (defaultValue && typeof defaultValue === 'object' && !Array.isArray(defaultValue)) {
        if (this.parsedOptionObjects.includes(prop)) continue
        options[prop] = this.parseOptionObject(prop, defaultValue) ?? defaultValue
      } else {
        options[prop] = this.parseOption(prop) ?? defaultValue
      }
    }

    return deepMerge(options, this.derivedOptions(options))
  }

  /**
   * Logs a message.
   *
   * @param {...*} args The message arguments.
   */
  log(...args) {
    console.debug(`${this.constructor.name}:`, ...args)
  }

  /**
   * Logs an error message.
   *
   * @param {...*} args The error message arguments.
   * @returns {boolean}
   */
  error(...args) {
    console.error(`${this.constructor.name} Error:`, ...args)
    return false
  }
}

/**
 * GWObservedElement
 *
 * @class
 * @abstract
 */
export class GWObservedElement extends GWElement {
  constructor() {
    super()

    this.observerList = new Map()
    this.observers = new Map()
  }

  /**
   * Initializes the element.
   */
  init() {
    super.init()
    this.initObservers()
  }

  /**
   * Destroys the element.
   *
   * @param {boolean} [disconnectObservers=true] Indicates whether to disconnect observers.
   */
  destroy(disconnectObservers = true) {
    super.destroy()

    if (disconnectObservers) {
      this.disconnectObservers()
    }
  }

  /**
   * Resets the element.
   */
  reset() {
    this.destroy(true)
    this.init()
  }

  /**
   * Returns an observer of a specific type for a specific target.
   *
   * @param {string} type The observer type.
   * @param {Object} target The observer target.
   * @returns {MutationObserver|IntersectionObserver|ResizeObserver|undefined} The observer, if found; otherwise, undefined.
   */
  getObserver(type, target) {
    const observers = this.observers.get(type)

    if (!observers) {
      return this.error(
        `Failed to get observer, no observer of type ${type} found`,
      )
    }

    const observer = observers.get(target)

    if (!observer) {
      return this.error(
        `Failed to get ${type} observer, no observer found for target`,
        target,
      )
    }

    return observer
  }

  /**
   * Adds an observer for a specific target and type.
   *
   * @param {Object} target The observer target.
   * @param {string} type The observer type.
   * @param {Object} data The observer data.
   */
  addObserver(target, type, data) {
    if (!data.callback) {
      return this.error(`Failed to add ${type} observer, no callback provided`)
    }

    data.callback = data.callback.bind(this)

    const existingObservers = this.observerList.get(type) || new Map()
    const observers = [...existingObservers, [target, data]]

    this.observerList.set(type, observers)
  }

  /**
   * Initializes all observers.
   * @private
   */
  initObservers() {
    this.log('Initializing observers')
    for (const [type, observers] of this.observerList) {
      for (const [target, data] of observers) {
        this.initObserver(type, target, data)
      }
    }
  }

  /**
   * Initializes an observer of a specific type for a specific target.
   * @private
   *
   * @param {string} type The observer type.
   * @param {Object} target The observer target.
   * @param {Object} data The observer data.
   */
  initObserver(type, target, data) {
    let observer = null

    switch (type) {
      case 'mutation':
        observer = this.initMutationObserver(target, data)
        break
      case 'intersection':
        observer = this.initIntersectionObserver(target, data)
        break
      case 'resize':
        observer = this.initResizeObserver(target, data)
        break
    }

    if (!observer) {
      return this.error(
        `Failed to initialize observer, the provided type "${type}" is not supported`,
      )
    }

    const existingObservers = this.observers.get(type) || new Map()
    const observers = [...existingObservers, [target, observer]]

    this.observers.set(type, observers)
  }

  /**
   * Processes observer options.
   * @private
   *
   * @param {Object} [options={}] The observer options.
   * @returns {Object} The processed observer options.
   */
  processObserverOptions(options = {}) {
    const processed = {}
    for (const key of Object.keys(options)) {
      processed[key] =
        typeof options[key] === 'function' ? options[key]() : options[key]
    }

    return processed
  }

  /**
   * Adds a mutation observer for a specific target and callback.
   *
   * @param {Object} target The observer target.
   * @param {Function} callback The observer callback.
   * @param {Object} [options={}] The observer options.
   */
  addMutationObserver(
    target,
    callback,
    { mutationType = 'childList', options = {} } = {},
  ) {
    this.addObserver(target, 'mutation', {
      callback,
      mutationType,
      options: {
        ...options,
        [mutationType]: true,
      },
    })
  }

  /**
   * Initializes a mutation observer for a specific target and options.
   * @private
   *
   * @param {Object} target The observer target.
   * @param {Object} data The observer data.
   * @returns {MutationObserver} The initialized mutation observer.
   */
  initMutationObserver(target, { mutationType, callback, options } = {}) {
    const observer = new MutationObserver((mutations, observer) => {
      for (const mutation of mutations) {
        if (mutation.type === mutationType) {
          callback(mutation, observer)
        }
      }
    })

    this.observe(observer, target, this.processObserverOptions(options))

    return observer
  }

  /**
   * Adds an intersection observer for a specific target and callback.
   *
   * @param {Object} target The observer target.
   * @param {Function} callback The observer callback.
   * @param {Object} [options={}] The observer options.
   */
  addIntersectionObserver(
    target,
    callback,
    {
      onIntersect = true,
      root = null,
      rootMargin = '0px 0px 0px 0px',
      threshold = 0.0,
    } = {},
  ) {
    this.addObserver(target, 'intersection', {
      callback,
      onIntersect,
      options: { root, rootMargin, threshold },
    })
  }

  /**
   * Initializes an intersection observer for a specific target and options.
   * @private
   *
   * @param {Object} target The observer target.
   * @param {Object} data The observer data.
   * @returns {IntersectionObserver} The initialized intersection observer.
   */
  initIntersectionObserver(target, { callback, onIntersect, options } = {}) {
    const observer = new IntersectionObserver((entries, observer) => {
      for (const entry of entries) {
        if (entry.isIntersecting || !onIntersect) {
          callback(entry, observer)
        }
      }
    }, this.processObserverOptions(options))

    this.observe(observer, target)

    return observer
  }

  /**
   * Adds a resize observer for a specific target and callback.
   *
   * @param {Object} target The observer target.
   * @param {Function} callback The observer callback.
   * @param {Object} [options={}] The observer options.
   */
  addResizeObserver(target, callback, { debounced = true, options } = {}) {
    this.addObserver(target, 'resize', { callback, debounced, options })
  }

  /**
   * Initializes a resize observer for a specific target and options.
   * @private
   *
   * @param {Object} target The observer target.
   * @param {Object} data The observer data.
   * @returns {ResizeObserver} The initialized resize observer.
   */
  initResizeObserver(target, { callback, debounced, options } = {}) {
    const resize = (entries, observer) => {
      for (const entry of entries) {
        callback(entry, observer)
      }
    }

    const observer = new ResizeObserver(
      debounced ? debounce(resize, RESIZE_DEBOUNCE_TIMER) : resize,
    )

    this.observe(observer, target, this.processObserverOptions(options))

    return observer
  }

  /**
   * Disconnects all observers.
   * @private
   */
  disconnectObservers() {
    for (const [, observers] of this.observers) {
      for (const [, observer] of observers) {
        observer.disconnect()
      }
    }

    this.observers.clear()
  }

  /**
   * Normalizes observer targets.
   * @private
   *
   * @param {Object|Object[]} target The observer target.
   * @returns {Object[]} The normalized observer targets.
   */
  normalizeTargets(target) {
    return isIterable(target) ? [...target] : [target]
  }

  /**
   * Observes a set of targets with an observer.
   * @private
   *
   * @param {MutationObserver|IntersectionObserver|ResizeObserver} observer The observer.
   * @param {Object|Object[]} targets The observer targets.
   * @param {Object} [options={}] The observer options.
   */
  observe(observer, targets, options = false) {
    const normalizedTargets = this.normalizeTargets(targets)

    for (const target of normalizedTargets) {
      if (options !== false) {
        observer.observe(target, options)
      } else {
        observer.observe(target)
      }
    }
  }
}

export const flattenEditorEventData = (data, [key, value], parentKey = null) => {
  // Prepend parent key if passed
  if (parentKey) key = `${parentKey}:${key}`

  // Reduce nested objects to single level
  if (typeof value === 'object')
    return reduceObject(value, flattenEditorEventData, data, key)

  // Prepend with shopify prefix if not already present
  if (!key.includes('shopify')) key = `shopify:${key}`

  return { ...data, [key]: value }
}
